"use client";

import { api } from "@agroproj/backend/convex/_generated/api";
import type { FeatureFlagKey } from "@agroproj/backend/convex/constants/featureFlags";
import { useQuery } from "convex/react";
import { useEffect, useState } from "react";

import { useConvexAuthReady } from "@/hooks/use-convex-auth-ready";
import { dateTimeFormat } from "@/lib/date-format";

const FEATURE_FLAG_NOW_REFRESH_MS = 60_000;

function bucketFeatureFlagNow(timestamp: number) {
  return Math.floor(timestamp / 60_000) * 60_000;
}

function useFeatureFlagNow() {
  const [now, setNow] = useState(() => bucketFeatureFlagNow(Date.now()));

  useEffect(() => {
    const interval = window.setInterval(() => {
      setNow(bucketFeatureFlagNow(Date.now()));
    }, FEATURE_FLAG_NOW_REFRESH_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setNow(bucketFeatureFlagNow(Date.now()));
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return now;
}

export function useConvexFeatureFlag(key: FeatureFlagKey | string) {
  const { canQuery } = useConvexAuthReady();
  const now = useFeatureFlagNow();
  const result = useQuery(api.features.queries.isEnabled, canQuery ? { key, now } : "skip");
  // Each clock tick changes the query args. Keep the last answer for this key while the
  // refreshed query loads so flag-gated trees do not unmount and lose state every minute.
  const [last, setLast] = useState<{ key: string; enabled: boolean } | null>(null);
  const current = !canQuery ? null : result === undefined ? last : { key, enabled: result };
  if (current?.key !== last?.key || current?.enabled !== last?.enabled) {
    setLast(current);
  }
  const enabled = current?.key === key ? current.enabled : undefined;

  return {
    enabled: enabled ?? false,
    isLoading: enabled === undefined,
  };
}

export function useConvexFeatureFlagStatus(key: FeatureFlagKey | string) {
  const { canQuery } = useConvexAuthReady();
  const now = useFeatureFlagNow();
  const result = useQuery(api.features.queries.getStatus, canQuery ? { key, now } : "skip");

  return {
    status: result,
    isLoading: result === undefined,
  };
}

/** Shared clock for admin override expiration display. */
export function useFeatureFlagExpirationClock() {
  return useFeatureFlagNow();
}

export function formatFeatureFlagExpiration(expiresAt: number | undefined, now: number) {
  if (expiresAt === undefined) {
    return null;
  }

  const expired = expiresAt < now;
  const label = dateTimeFormat(expiresAt);

  return { expired, label };
}
